import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
} from "@nebular/theme";
import { Course } from "app/@core/types";
import { DayjsService } from "app/@core/utils/dayjs.service";
import dayjs, { Dayjs } from "dayjs";
import { environment } from "environments/environment";
import { Subject, timer } from "rxjs";
import { takeWhile, tap } from "rxjs/operators";
import { CourseService } from "../../../@core/services/course.service";
import { PwycService } from "../../../@core/services/pwyc.service";
import { ngVideoDirective } from "../../directives";
import { SanitizeHtmlPipe } from "../../pipes";

type PwycState = {
  title: string;
  video: { id: string; type: string };
  skipdays: number;
  today: dayjs.Dayjs;
  startdate: dayjs.Dayjs | null;
  enddate: dayjs.Dayjs | null;
  isSkipDate: dayjs.Dayjs | null;
  isFinalDate: dayjs.Dayjs | null;
  days: number;
  isSkipped: boolean;
  showForm: boolean;
};

@Component({
  standalone: true,
  selector: "ngx-pwyc-cff",
  templateUrl: "pwyc-cff.component.html",
  styleUrls: ["./pwyc-cff.component.scss"],
  imports: [
    ReactiveFormsModule,
    NbCardModule,
    NbIconModule,
    ngVideoDirective,
    SanitizeHtmlPipe,
    NbInputModule,
    NbCheckboxModule,
    NbSpinnerModule,
  ],
})
export class PwycCFFComponent implements OnDestroy {
  private formBuilder = inject(FormBuilder);
  private courseService = inject(CourseService);
  private pwycService = inject(PwycService);
  private dialogService = inject(NbDialogService);
  private dayjs = inject(DayjsService);
  private destroy$: Subject<void> = new Subject<void>();

  @ViewChild("dialog", { static: true }) dialog!: TemplateRef<any>;
  @ViewChild("confirmdialog", { static: true })
  confirmdialog!: TemplateRef<any>;
  @ViewChild("dialogDiv", { read: ElementRef })
  dialogDiv: ElementRef<HTMLElement>;

  error: string;
  submitted = signal<boolean>(false);
  isManual = signal<boolean>(false);
  showMessage = signal<boolean>(true);
  watchedVideo = signal<boolean>(false);
  msg: string;
  errorMsg: string;
  dialogRef: NbDialogRef<any>;
  confirmRef: NbDialogRef<any>;
  counter = 25;

  courseid = input.required<number>();
  @Output() pwycOutput = new EventEmitter();

  constructor() {
    // This effect watches the VM. Whenever the video ID or showForm status changes, it runs.
    effect(() => {
      const state = this.pwycConfig();
      const videoId = state.video.id;
      const shouldShowForm = state.showForm;

      untracked(() => {
        const storedData = localStorage.getItem(`${videoId}_watched`);
        this.watchedVideo.set(JSON.parse(storedData) || false);

        if (shouldShowForm) {
          this.pwycOutput.emit(true);
          this.open(this.dialog);
        }
      });
    });
  }

  course = computed<Course | null>(() => {
    const courses = this.courseService.courses();
    return courses?.find((c) => c.id === this.courseid()) ?? null;
  });

  pwycConfig = computed<PwycState>(() => {
    const course = this.course();
    if (!course) return null;

    let title = "the Course Freedom Formula";
    let video = { id: "0arz6r4dke", type: "wistia" };
    let skipdays = 28;

    let today = this.dayjs.now();
    if (environment.baseUrl.includes("staging")) {
      today = this.dayjs.parseWithTZ(course.options?.today || undefined);
    }

    let startdate = null;
    let enddate = null;
    let isSkipDate = null;
    let isFinalDate = null;
    let days = 0;
    let isSkipped = false;
    let showForm = false;

    if (course.course_enddate) {
      startdate = this.dayjs.parseWithTZ(course.course_startdate);
      enddate = this.dayjs.parseWithTZ(course.course_enddate);

      isSkipDate = enddate.isAfter(today)
        ? enddate.add(1, "days")
        : today.add(1, "days");

      isFinalDate = startdate.add(skipdays, "days");
      days = enddate.diff(startdate, "days");

      // 3. Visibility Logic
      const isPastEnd = today.isAfter(enddate);
      const inRangeA = skipdays - 1 < days;
      const inRangeB = days > 13 && days <= 27;

      if (inRangeA || inRangeB) {
        isSkipped = inRangeA; // logic based on your previous code
        showForm = isPastEnd;
      }
    }

    return {
      title,
      video,
      skipdays,
      today,
      startdate,
      enddate,
      isSkipDate,
      isFinalDate,
      days,
      isSkipped,
      showForm,
    };
  });

  pwycForm = computed(() => {
    return this.formBuilder.group({
      pwyc: new FormControl(375, [
        Validators.required,
        Validators.min(10),
        Validators.pattern("^-?[0-9]\\d*(\\.\\d{1,2})?$"),
      ]),
      accept: new FormControl(false, Validators.requiredTrue),
    });
  });

  hasNotPaid(coursedate: Dayjs): boolean {
    let todate = this.dayjs.parseWithTZ("2031-12-31 23:59:59");
    return coursedate.valueOf() != todate.valueOf();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.pwycForm().controls;
  }

  openManual(): void {
    // this.isSkipped = true;
    this.isManual.set(true);
    this.open(this.dialog);
  }

  open(dialog: TemplateRef<any>): void {
    this.dialogRef = this.dialogService.open(dialog, {
      autoFocus: false,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      hasBackdrop: true,
    });
  }

  confirmOpen(confirmdialog: TemplateRef<any>): void {
    this.confirmRef = this.dialogService.open(confirmdialog, {
      autoFocus: false,
      closeOnBackdropClick: false,
      closeOnEsc: false,
      hasBackdrop: true,
    });
  }

  confirmClose(): void {
    this.confirmRef.close();
  }

  onFormSubmit(): void {
    if (this.pwycForm().valid) {
      this.confirmOpen(this.confirmdialog);
    }
  }

  async updatePayment(): Promise<void> {
    this.confirmRef.close();
    var pwycData = {
      amount: this.pwycForm().get("pwyc").value,
      course: this.course().slug,
    };
    this.submitted.set(true);
    const result = this.pwycService.updatePwyc<boolean>(pwycData);
    if (result) {
      this.showMessage.set(false);
      this.msg =
        "<p>Thanks for choosing your Pay What You Can amount! Your first payment of this amount will be drawn this week, with 3 further monthly payments of the same amount to follow.</p><p>You now have permanent access to the full " +
        this.pwycConfig().title +
        " program -- so continue making good use of it, and enjoy the impact, income, and freedom you gain from your hybrid course!</p>";
      this.reloadPage();
    } else {
      if (this.pwycConfig().days > 28) {
        this.errorMsg =
          "<p>There was an error processing your Pay What You Can amount but don't worry! We've noted the error and our support team will reach out to you to complete your payment.</p><p>In the meantime, you can click on the \"I'm not ready to decide yet\" link to hide this popup (prior to July 1).</p>";
      } else {
        this.errorMsg =
          "<p>There was an error processing your Pay What You Can amount. Please contact support@mirasee.com so we can complete your payment and extend your course access.</p>";
      }
    }
    this.confirmRef.close();
    this.courseService.reloadCourses();
    this.reloadPage();
  }

  onSkip(): void {
    const skipData = {
      nextdate: this.pwycConfig().isSkipDate.format("YYYY-MM-DD HH:mm:ss"),
      course: this.course().slug,
    };
    const result = this.pwycService.skipPwyc<boolean>(skipData);
    if (result) {
      this.showMessage.set(false);
      this.msg =
        "Thanks for letting us know you're not quite ready to choose your pay-what-you-can amount! We will ask you again soon (within the next 14 days), so you have a little longer to make use of the program before you decide.";
    }
    this.courseService.reloadCourses();
    this.reloadPage();
  }

  closeDialog(): void {
    this.dialogRef.close();
    window.location.reload();
  }

  reloadPage(): void {
    timer(1000, 1000) //Initial delay 1 seconds and interval countdown also 1 second
      .pipe(
        takeWhile(() => this.counter > 0),
        tap(() => this.counter--)
      )
      .subscribe(() => {
        if (this.counter == 0) {
          this.dialogRef.close();
          window.location.reload();
        }
      });
  }

  videoOutput(video: any): void {
    video.bind("secondchange", (s: number) => {
      if (s > 342) {
        this.showVidoContent();
      }
    });
    video.bind("end", () => this.showVidoContent());
  }

  showVidoContent(): void {
    if (!this.watchedVideo()) {
      this.watchedVideo.set(true);
      localStorage.setItem(this.pwycConfig().video.id + "_watched", "true");
      this.dialogDiv.nativeElement.click();
    }
  }

  ngOnDestroy() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
